"use client";

import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  ShieldCheck,
  RotateCcw,
  Search,
  Eye,
  Users,
  Coffee,
  Gift,
  Wine,
  UserCheck,
  Receipt,
  Sparkles,
  Utensils,
  DollarSign,
  CreditCard,
  BarChart2,
  Coins,
  Trophy,
  MessageSquare,
  Percent,
  Clock,
  User,
  CheckCheck,
  XCircle,
  Briefcase,
  Settings
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "react-hot-toast";
import { permissionService } from "../apis/permissionService";
import { SIDEBAR_TABS } from "../constants/sidebarTabs";

/**
 * Transaction modules configuration:
 * Pairs each module's View permission key with its Actions column permission key.
 */
const TRANSACTION_MODULES = [
  {
    viewKey: "tips_dealer",
    actionKey: "tips_dealer_actions",
    title: "Dealer Tips",
    href: "/dealer-tips",
    desc: "Dealer tokes and dynamic tax calculations",
    Icon: Users,
    color: "text-blue-600 bg-blue-50"
  },
  {
    viewKey: "tips_waitress",
    actionKey: "tips_waitress_actions",
    title: "Server/waitress Tips",
    href: "/waitress-tips",
    desc: "Server & waitress tip outs, dynamic tax & owe to Toast",
    Icon: Coffee,
    color: "text-amber-600 bg-amber-50"
  },
  {
    viewKey: "tips_floor",
    actionKey: "tips_floor_actions",
    title: "Floor Tips",
    href: "/floor-tips",
    desc: "Floor Supervisor tip out records",
    Icon: Gift,
    color: "text-purple-600 bg-purple-50"
  },
  {
    viewKey: "tips_bartender",
    actionKey: "tips_bartender_actions",
    title: "Bartender Tips",
    href: "/bartender-tips",
    desc: "Bartender tip transactions & owe to Toast",
    Icon: Wine,
    color: "text-rose-600 bg-rose-50"
  },
  {
    viewKey: "tips_host",
    actionKey: "tips_host_actions",
    title: "Host Tips",
    href: "/host-tips",
    desc: "Host & front desk tip records",
    Icon: UserCheck,
    color: "text-indigo-600 bg-indigo-50"
  },
  {
    viewKey: "tips_cashier",
    actionKey: "tips_cashier_actions",
    title: "Cashier Tips",
    href: "/cashier-tips",
    desc: "Cashier cage tip outs",
    Icon: Receipt,
    color: "text-emerald-600 bg-emerald-50"
  },
  {
    viewKey: "tips_janitor",
    actionKey: "tips_janitor_actions",
    title: "Custodian/Janitor Tips",
    href: "/janitor-tips",
    desc: "Custodian & Janitor tip records",
    Icon: Sparkles,
    color: "text-teal-600 bg-teal-50"
  },
  {
    viewKey: "tips_cook",
    actionKey: "tips_cook_actions",
    title: "Cook Tips",
    href: "/cook-tips",
    desc: "Kitchen & Cook tip records",
    Icon: Utensils,
    color: "text-orange-600 bg-orange-50"
  },
  {
    viewKey: "tips_other",
    actionKey: "tips_other_actions",
    title: "Other Positions Tips",
    href: "/other-tips",
    desc: "Tip records for custom & additional employee roles",
    Icon: DollarSign,
    color: "text-amber-700 bg-amber-100"
  },
  {
    viewKey: "promos",
    actionKey: "promos_actions",
    title: "Promos",
    href: "/promos",
    desc: "Promotional giveaways and receipt tracking",
    Icon: Gift,
    color: "text-pink-600 bg-pink-50"
  },
  {
    viewKey: "petty_cash",
    actionKey: "petty_cash_actions",
    title: "Petty Cash",
    href: "/petty-cash",
    desc: "Club expenses and webcam receipt uploads",
    Icon: CreditCard,
    color: "text-cyan-600 bg-cyan-50"
  },
  {
    viewKey: "tournament_house_fees",
    actionKey: "tournament_house_fees_actions",
    title: "Tournament House Fees",
    href: "/tournament-house-fees",
    desc: "House fee allocations and transactions",
    Icon: BarChart2,
    color: "text-violet-600 bg-violet-50"
  },
  {
    viewKey: "poker_cash_box",
    actionKey: null,
    title: "Poker Cash Box",
    href: "/poker-cash-box",
    desc: "Denomination chip counters and save logs",
    Icon: CreditCard,
    color: "text-slate-600 bg-slate-100"
  },
  {
    viewKey: "tournament_poker_chips",
    actionKey: null,
    title: "Tournament Poker Chips",
    href: "/tournament-poker-chips",
    desc: "Tournament chip inventory records",
    Icon: Coins,
    color: "text-yellow-700 bg-yellow-50"
  }
];

/** Icon resolver for general category tabs */
const getTabIcon = (key) => {
  switch (key) {
    case "tournament_leaderboard": return Trophy;
    case "analytics_overview": return BarChart2;
    case "user_management": return Users;
    case "employee_management": return Briefcase;
    case "payroll_sales_analytics": return BarChart2;
    case "payroll_salary": return DollarSign;
    case "payroll_bonuses": return Gift;
    case "token_reserves": return Coins;
    case "tables_time": return Clock;
    case "tax_management": return Percent;
    case "player_profiles": return User;
    case "sms_logs": return MessageSquare;
    default: return ShieldCheck;
  }
};

/**
 * Accessible Toggle Switch Component
 */
function ToggleSwitch({ checked, onChange, disabled = false, activeColor = "bg-emerald-600", label }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={onChange}
      className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-150 ease-in-out focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:ring-offset-1 ${
        disabled
          ? "opacity-35 cursor-not-allowed bg-gray-200"
          : checked
          ? activeColor
          : "bg-gray-300 hover:bg-gray-400"
      }`}
      title={label}
    >
      <span
        aria-hidden="true"
        className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow-sm ring-0 transition duration-150 ease-in-out ${
          checked ? "translate-x-4" : "translate-x-0"
        }`}
      />
    </button>
  );
}

export function PermissionManagementModal({
  isOpen,
  onClose,
  targetType = "role", // "role" | "user"
  targetId, // roleId or userId
  targetName, // Role name or User name
  userRoleName, // Optional: role name if targetType === "user"
  onPermissionsUpdated,
  role,
  user
}) {
  const effectiveTargetType = role ? "role" : (user ? "user" : targetType);
  const effectiveTargetId = targetId ?? (role?.id ?? role?.role_id ?? user?.id ?? user?.userid);
  const effectiveTargetName = targetName ?? (role?.role ?? role?.role_name ?? user?.name ?? "");

  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeCategoryTab, setActiveCategoryTab] = useState("All");

  // Fetch permissions when modal opens
  useEffect(() => {
    if (!isOpen || !effectiveTargetId) return;

    const fetchPerms = async () => {
      setLoading(true);
      try {
        let res;
        if (effectiveTargetType === "role") {
          res = await permissionService.getRolePermissions(effectiveTargetId);
        } else {
          res = await permissionService.getUserPermissions(effectiveTargetId);
        }

        if (res.success && res.data) {
          const loadedPerms = res.data.permissions || res.data;
          const fullPerms = {};
          SIDEBAR_TABS.forEach((t) => {
            fullPerms[t.key] = Boolean(loadedPerms[t.key]);
          });
          setPermissions(fullPerms);
        } else {
          toast.error(res.error || "Failed to load permissions");
        }
      } catch (err) {
        console.error("Error fetching permissions:", err);
        toast.error("Failed to load permissions");
      } finally {
        setLoading(false);
      }
    };

    fetchPerms();
    setSearchTerm("");
    setActiveCategoryTab("All");
  }, [isOpen, effectiveTargetId, effectiveTargetType]);

  // Toggle single permission key
  const toggleKey = useCallback((key) => {
    setPermissions((prev) => ({
      ...prev,
      [key]: !prev[key]
    }));
  }, []);

  // Set multiple keys at once
  const setKeys = useCallback((keyMap) => {
    setPermissions((prev) => ({
      ...prev,
      ...keyMap
    }));
  }, []);

  // When a Transaction module view is toggled:
  const handleToggleModuleView = (mod) => {
    const willBeChecked = !permissions[mod.viewKey];
    const update = { [mod.viewKey]: willBeChecked };
    // If turning OFF view, also turn OFF actions column (cannot edit a page you cannot see)
    if (!willBeChecked && mod.actionKey) {
      update[mod.actionKey] = false;
    }
    setKeys(update);
  };

  // When a Transaction module actions column is toggled:
  const handleToggleModuleAction = (mod) => {
    if (!mod.actionKey) return;
    const willBeChecked = !permissions[mod.actionKey];
    const update = { [mod.actionKey]: willBeChecked };
    // If turning ON actions, automatically ensure page view is also enabled
    if (willBeChecked) {
      update[mod.viewKey] = true;
    }
    setKeys(update);
  };

  // Batch actions for Transactions
  const handleTransactionBatch = (mode) => {
    const update = {};
    TRANSACTION_MODULES.forEach((m) => {
      if (mode === "all_view") {
        update[m.viewKey] = true;
      } else if (mode === "all_actions") {
        update[m.viewKey] = true;
        if (m.actionKey) update[m.actionKey] = true;
      } else if (mode === "view_only") {
        update[m.viewKey] = true;
        if (m.actionKey) update[m.actionKey] = false;
      } else if (mode === "disable_all") {
        update[m.viewKey] = false;
        if (m.actionKey) update[m.actionKey] = false;
      }
    });
    setKeys(update);
  };

  // Select all tabs globally
  const selectAll = () => {
    const updated = {};
    SIDEBAR_TABS.forEach((t) => {
      updated[t.key] = true;
    });
    setPermissions(updated);
  };

  // Deselect all tabs globally
  const deselectAll = () => {
    const updated = {};
    SIDEBAR_TABS.forEach((t) => {
      updated[t.key] = false;
    });
    setPermissions(updated);
  };

  // Reset to default permissions
  const resetToDefault = async () => {
    if (!effectiveTargetId) return;
    try {
      setLoading(true);
      if (effectiveTargetType === "role") {
        const res = await permissionService.resetRolePermissions(effectiveTargetId);
        if (res.success && res.data?.permissions) {
          setPermissions(res.data.permissions);
          toast.success("Reset to role defaults");
        }
      } else {
        const res = await permissionService.getRolePermissions(effectiveTargetId);
        if (res.success && res.data?.permissions) {
          setPermissions(res.data.permissions);
          toast.success("Reset to user role defaults");
        }
      }
    } catch (e) {
      toast.error("Failed to reset permissions");
    } finally {
      setLoading(false);
    }
  };

  // Save changes to backend
  const handleSave = async () => {
    if (!effectiveTargetId) {
      toast.error("Invalid role or user identifier");
      return;
    }
    setIsSaving(true);
    try {
      let res;
      if (effectiveTargetType === "role") {
        res = await permissionService.updateRolePermissions(effectiveTargetId, permissions);
      } else {
        res = await permissionService.updateUserPermissions(effectiveTargetId, permissions);
      }

      if (res.success) {
        toast.success("Permissions updated successfully!");
        try {
          const userStr = localStorage.getItem("user");
          if (userStr) {
            const u = JSON.parse(userStr);
            const matchesRole = effectiveTargetType === "role" && (u.roleid === effectiveTargetId || u.role === effectiveTargetName);
            const matchesUser = effectiveTargetType === "user" && (u.userid === effectiveTargetId || u.id === effectiveTargetId);
            if (matchesRole || matchesUser) {
              u.permissions = permissions;
              localStorage.setItem("user", JSON.stringify(u));
            }
          }
          window.dispatchEvent(
            new CustomEvent("permissions_updated", {
              detail: { targetType: effectiveTargetType, targetId: effectiveTargetId, permissions }
            })
          );
        } catch (_) {}
        onPermissionsUpdated?.(permissions);
        onClose();
      } else {
        toast.error(res.error || "Failed to save permissions");
      }
    } catch (err) {
      console.error("Error saving permissions:", err);
      toast.error("Failed to save permissions");
    } finally {
      setIsSaving(false);
    }
  };

  // Standard non-action tabs
  const standardTabs = useMemo(() => {
    return SIDEBAR_TABS.filter((t) => !t.isAction && t.category !== "Transactions");
  }, []);

  // Filtered standard tabs
  const filteredStandardTabs = useMemo(() => {
    if (!searchTerm.trim()) return standardTabs;
    const term = searchTerm.toLowerCase();
    return standardTabs.filter(
      (t) =>
        t.label.toLowerCase().includes(term) ||
        t.category.toLowerCase().includes(term) ||
        t.href.toLowerCase().includes(term)
    );
  }, [standardTabs, searchTerm]);

  // Filtered transaction modules
  const filteredTransactionModules = useMemo(() => {
    if (!searchTerm.trim()) return TRANSACTION_MODULES;
    const term = searchTerm.toLowerCase();
    return TRANSACTION_MODULES.filter(
      (m) =>
        m.title.toLowerCase().includes(term) ||
        m.href.toLowerCase().includes(term) ||
        m.desc.toLowerCase().includes(term)
    );
  }, [searchTerm]);

  // Statistics
  const pageAccessCount = useMemo(() => {
    const pageKeys = [
      ...standardTabs.map((t) => t.key),
      ...TRANSACTION_MODULES.map((m) => m.viewKey)
    ];
    return pageKeys.filter((k) => permissions[k]).length;
  }, [standardTabs, permissions]);

  const totalPageCount = useMemo(() => {
    return standardTabs.length + TRANSACTION_MODULES.length;
  }, [standardTabs]);

  const actionsCount = useMemo(() => {
    return TRANSACTION_MODULES.filter((m) => m.actionKey && permissions[m.actionKey]).length;
  }, [permissions]);

  const totalActionCount = useMemo(() => {
    return TRANSACTION_MODULES.filter((m) => m.actionKey).length;
  }, []);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col p-0 overflow-hidden bg-white shadow-2xl rounded-xl">
        {/* Header */}
        <DialogHeader className="px-6 pt-5 pb-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 via-white to-gray-50">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-yellow-100/80 rounded-xl text-yellow-800 border border-yellow-200/60 shadow-sm">
                <ShieldCheck className="h-6 w-6" />
              </div>
              <div>
                <DialogTitle className="text-xl font-bold text-gray-900 tracking-tight">
                  Role Permissions & Access Control
                </DialogTitle>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-xs text-gray-500">Configuring for:</span>
                  <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold bg-gray-900 text-yellow-400">
                    {effectiveTargetName}
                  </span>
                  {userRoleName && (
                    <span className="text-xs text-gray-400 font-medium">({userRoleName})</span>
                  )}
                  <span className="text-xs text-gray-300">•</span>
                  <span className="text-xs font-medium text-gray-600">
                    {effectiveTargetType === "role" ? "Role Baseline" : "User Custom Override"}
                  </span>
                </div>
              </div>
            </div>

            {/* Quick Metrics */}
            <div className="flex items-center gap-2">
              <div className="px-3 py-1.5 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-medium text-center">
                <span className="font-bold text-sm block leading-none">{pageAccessCount} / {totalPageCount}</span>
                <span className="text-[10px] text-emerald-600 uppercase tracking-wide">Pages Allowed</span>
              </div>
              <div className="px-3 py-1.5 rounded-lg bg-blue-50 border border-blue-200 text-blue-800 text-xs font-medium text-center">
                <span className="font-bold text-sm block leading-none">{actionsCount} / {totalActionCount}</span>
                <span className="text-[10px] text-blue-600 uppercase tracking-wide">Actions Allowed</span>
              </div>
            </div>
          </div>

          {/* Controls Bar: Category Pills + Search + Global Quick Buttons */}
          <div className="flex flex-wrap items-center justify-between gap-2.5 mt-4 pt-3 border-t border-gray-200/70">
            {/* Category Tabs */}
            <div className="flex items-center gap-1 overflow-x-auto pb-0.5">
              {[
                { id: "All", label: "All Modules", count: totalPageCount },
                { id: "Transactions", label: "Transactions", count: TRANSACTION_MODULES.length },
                { id: "Management", label: "Management", count: 9 },
                { id: "Table Tracker", label: "Table Tracker", count: 2 },
                { id: "Tournament", label: "Tournament", count: 1 }
              ].map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveCategoryTab(tab.id)}
                  className={`px-3 py-1 rounded-lg text-xs font-medium transition-all ${
                    activeCategoryTab === tab.id
                      ? "bg-gray-900 text-white shadow-sm"
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-900"
                  }`}
                >
                  {tab.label}
                  <span
                    className={`ml-1.5 text-[10px] px-1.5 py-0.2 rounded-full font-semibold ${
                      activeCategoryTab === tab.id
                        ? "bg-yellow-400 text-black"
                        : "bg-gray-200 text-gray-700"
                    }`}
                  >
                    {tab.count}
                  </span>
                </button>
              ))}
            </div>

            {/* Global Actions */}
            <div className="flex items-center gap-1.5 ml-auto">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={selectAll}
                className="h-7 text-xs px-2.5 text-gray-700 hover:bg-gray-100"
              >
                <CheckCheck className="h-3.5 w-3.5 mr-1 text-emerald-600" />
                Select All
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={deselectAll}
                className="h-7 text-xs px-2.5 text-gray-700 hover:bg-gray-100"
              >
                <XCircle className="h-3.5 w-3.5 mr-1 text-red-500" />
                Clear All
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={resetToDefault}
                className="h-7 text-xs px-2 text-gray-600 hover:text-gray-900"
                title="Reset to canonical role defaults"
              >
                <RotateCcw className="h-3.5 w-3.5 mr-1" />
                Defaults
              </Button>
            </div>
          </div>

          {/* Search Box */}
          <div className="relative mt-2.5">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
            <Input
              type="text"
              placeholder="Filter by module name, route URL, or description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 h-8 text-xs bg-white rounded-lg border-gray-200 shadow-inner"
            />
          </div>
        </DialogHeader>

        {/* Content Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6 bg-gray-50/40">
          {loading ? (
            <div className="flex flex-col justify-center items-center py-20">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-yellow-500"></div>
              <p className="text-xs text-gray-500 mt-2 font-medium">Loading permissions configuration...</p>
            </div>
          ) : (
            <>
              {/* ============================================================== */}
              {/* SECTION: TRANSACTIONS MATRIX                                  */}
              {/* ============================================================== */}
              {(activeCategoryTab === "All" || activeCategoryTab === "Transactions") && (
                <div className="border border-gray-200 rounded-xl overflow-hidden bg-white shadow-sm">
                  {/* Category Header with Matrix Legend & Batch Buttons */}
                  <div className="px-4 py-3 bg-gradient-to-r from-gray-50 via-slate-50 to-gray-50 border-b border-gray-200 flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <div className="flex items-center gap-2">
                        <CreditCard className="h-4 w-4 text-blue-600" />
                        <h3 className="text-sm font-bold text-gray-900">
                          Transactions Modules & Table Action Permissions
                        </h3>
                        <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 font-semibold">
                          {filteredTransactionModules.length} Modules
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5">
                        Manage page accessibility and independent Edit/Delete action columns for each module.
                      </p>
                    </div>

                    {/* Transaction Batch Controls */}
                    <div className="flex items-center gap-1.5">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleTransactionBatch("view_only")}
                        className="h-7 px-2.5 text-xs text-amber-700 bg-amber-50 hover:bg-amber-100 border border-amber-200/80 font-medium"
                        title="Enable all views but hide all Edit/Delete action columns"
                      >
                        <Eye className="h-3 w-3 mr-1" />
                        View-Only Mode
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleTransactionBatch("all_actions")}
                        className="h-7 px-2.5 text-xs text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200/80 font-medium"
                        title="Enable all views and unhide all action columns"
                      >
                        <CheckCheck className="h-3 w-3 mr-1" />
                        Allow All Actions
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleTransactionBatch("disable_all")}
                        className="h-7 px-2 text-xs text-gray-600 hover:bg-gray-200/80"
                        title="Disable all transaction modules"
                      >
                        Disable All
                      </Button>
                    </div>
                  </div>

                  {/* Transactions Matrix Table */}
                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-xs">
                      <thead className="bg-gray-50/90 text-gray-600 font-semibold border-b border-gray-200 uppercase tracking-wider text-[11px]">
                        <tr>
                          <th className="py-2.5 px-4">Transaction Module & URL</th>
                          <th className="py-2.5 px-4 w-44 text-center">Page Access (View)</th>
                          <th className="py-2.5 px-4 w-48 text-center">Actions Column (Edit / Delete)</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {filteredTransactionModules.length === 0 ? (
                          <tr>
                            <td colSpan={3} className="py-8 text-center text-gray-400">
                              No transaction modules matching "{searchTerm}"
                            </td>
                          </tr>
                        ) : (
                          filteredTransactionModules.map((mod) => {
                            const isViewAllowed = Boolean(permissions[mod.viewKey]);
                            const isActionAllowed = mod.actionKey ? Boolean(permissions[mod.actionKey]) : false;
                            const IconComponent = mod.Icon;

                            return (
                              <tr
                                key={mod.viewKey}
                                className={`transition-colors ${
                                  isViewAllowed
                                    ? isActionAllowed
                                      ? "bg-blue-50/20 hover:bg-blue-50/40"
                                      : "bg-emerald-50/15 hover:bg-emerald-50/30"
                                    : "bg-white hover:bg-gray-50/70 opacity-80"
                                }`}
                              >
                                {/* Module Info */}
                                <td className="py-3 px-4">
                                  <div className="flex items-start gap-3">
                                    <div className={`p-2 rounded-lg ${mod.color} shrink-0 mt-0.5`}>
                                      <IconComponent className="h-4 w-4" />
                                    </div>
                                    <div>
                                      <div className="flex items-center gap-2">
                                        <span className="font-semibold text-gray-900 text-sm">
                                          {mod.title}
                                        </span>
                                        <span className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 border border-gray-200">
                                          {mod.href}
                                        </span>
                                      </div>
                                      <p className="text-gray-500 text-xs mt-0.5 leading-snug">
                                        {mod.desc}
                                      </p>
                                    </div>
                                  </div>
                                </td>

                                {/* Column 1: Page View Access */}
                                <td className="py-3 px-4 text-center align-middle">
                                  <div className="flex flex-col items-center justify-center gap-1">
                                    <ToggleSwitch
                                      checked={isViewAllowed}
                                      onChange={() => handleToggleModuleView(mod)}
                                      activeColor="bg-emerald-600"
                                      label={`Toggle page access for ${mod.title}`}
                                    />
                                    <span
                                      className={`text-[11px] font-semibold ${
                                        isViewAllowed ? "text-emerald-700" : "text-gray-400"
                                      }`}
                                    >
                                      {isViewAllowed ? "Allowed" : "Blocked"}
                                    </span>
                                  </div>
                                </td>

                                {/* Column 2: Actions Column (Edit/Delete) */}
                                <td className="py-3 px-4 text-center align-middle">
                                  {mod.actionKey ? (
                                    <div className="flex flex-col items-center justify-center gap-1">
                                      <ToggleSwitch
                                        checked={isActionAllowed}
                                        disabled={!isViewAllowed}
                                        onChange={() => handleToggleModuleAction(mod)}
                                        activeColor="bg-blue-600"
                                        label={`Toggle actions column for ${mod.title}`}
                                      />
                                      <span
                                        className={`text-[11px] font-semibold ${
                                          !isViewAllowed
                                            ? "text-gray-300 italic"
                                            : isActionAllowed
                                            ? "text-blue-700"
                                            : "text-gray-400"
                                        }`}
                                      >
                                        {!isViewAllowed
                                          ? "Page Blocked"
                                          : isActionAllowed
                                          ? "Unhidden (Active)"
                                          : "Hidden (View Only)"}
                                      </span>
                                    </div>
                                  ) : (
                                    <div className="text-center text-gray-400 text-[11px] italic">
                                      — N/A (Balance only) —
                                    </div>
                                  )}
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* ============================================================== */}
              {/* SECTION: OTHER SYSTEM CATEGORIES (MANAGEMENT, TRACKER, TOURNAMENT) */}
              {/* ============================================================== */}
              {["Management", "Table Tracker", "Tournament"].map((category) => {
                if (activeCategoryTab !== "All" && activeCategoryTab !== category) {
                  return null;
                }

                const catTabs = filteredStandardTabs.filter((t) => t.category === category);
                if (catTabs.length === 0) return null;

                const catCheckedCount = catTabs.filter((t) => permissions[t.key]).length;
                const allChecked = catCheckedCount === catTabs.length;

                return (
                  <div
                    key={category}
                    className="border border-gray-200 rounded-xl overflow-hidden bg-white shadow-sm"
                  >
                    {/* Header */}
                    <div className="px-4 py-3 bg-gradient-to-r from-gray-50 via-slate-50 to-gray-50 border-b border-gray-200 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="p-1.5 rounded-lg bg-yellow-100 text-yellow-800">
                          {category === "Management" && <Settings className="h-4 w-4" />}
                          {category === "Table Tracker" && <MessageSquare className="h-4 w-4" />}
                          {category === "Tournament" && <Trophy className="h-4 w-4" />}
                        </div>
                        <h3 className="text-sm font-bold text-gray-900">{category} Modules</h3>
                        <span className="text-xs text-gray-500 font-medium">
                          ({catCheckedCount} of {catTabs.length} Allowed)
                        </span>
                      </div>

                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          const update = {};
                          catTabs.forEach((t) => {
                            update[t.key] = !allChecked;
                          });
                          setKeys(update);
                        }}
                        className="h-7 px-2.5 text-xs text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                      >
                        {allChecked ? "Uncheck Category" : "Allow All in Category"}
                      </Button>
                    </div>

                    {/* Standard Grid */}
                    <div className="divide-y divide-gray-100">
                      {catTabs.map((tab) => {
                        const isAllowed = Boolean(permissions[tab.key]);
                        const TabIcon = getTabIcon(tab.key);

                        return (
                          <div
                            key={tab.key}
                            className={`p-3.5 flex items-center justify-between gap-4 transition-colors ${
                              isAllowed ? "bg-emerald-50/15 hover:bg-emerald-50/30" : "hover:bg-gray-50/80"
                            }`}
                          >
                            <div className="flex items-start gap-3">
                              <div className="p-2 rounded-lg bg-gray-100 text-gray-700 shrink-0 mt-0.5">
                                <TabIcon className="h-4 w-4" />
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <span className="text-sm font-semibold text-gray-900">
                                    {tab.label}
                                  </span>
                                  <span className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 border border-gray-200">
                                    {tab.href}
                                  </span>
                                </div>
                                <p className="text-xs text-gray-500 mt-0.5">{tab.description}</p>
                              </div>
                            </div>

                            <div className="flex items-center gap-2.5 shrink-0">
                              <span
                                className={`text-xs font-semibold ${
                                  isAllowed ? "text-emerald-700" : "text-gray-400"
                                }`}
                              >
                                {isAllowed ? "Allowed" : "Blocked"}
                              </span>
                              <ToggleSwitch
                                checked={isAllowed}
                                onChange={() => toggleKey(tab.key)}
                                activeColor="bg-emerald-600"
                                label={`Toggle ${tab.label}`}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </div>

        {/* Footer */}
        <DialogFooter className="px-6 py-3.5 border-t border-gray-200 bg-gray-50 flex items-center justify-between sm:justify-between">
          <div className="text-xs text-gray-500 flex items-center gap-2">
            <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse"></span>
            <span>Changes update database & take effect dynamically across all screens.</span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isSaving}
              className="h-9 text-xs px-4"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleSave}
              disabled={isSaving || loading}
              className="h-9 text-xs px-5 bg-yellow-500 hover:bg-yellow-600 text-black font-semibold shadow-sm"
            >
              {isSaving ? "Saving..." : "Save Permissions"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default PermissionManagementModal;
